import { Ref, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SizeObservableSize } from './ResizeObserverHook';
import debounce from 'lodash/debounce';

export type ContainerBreakpointWidth = 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

const BREAKPOINTS: { [name in ContainerBreakpointWidth]: number } = {
   'x-small': 359,
   'small': 599,
   'medium': 959,
   'large': 1279,
   'x-large': Number.MAX_SAFE_INTEGER
};

const getCurrentBreakpoint = (width: number): ContainerBreakpointWidth => {

   let breakpointNameToUse: ContainerBreakpointWidth;
   for (breakpointNameToUse in BREAKPOINTS) {

      if (width < BREAKPOINTS[breakpointNameToUse]) {

         return breakpointNameToUse;
      }
   }

   return 'x-large';
};

export const compareBreakpoints = (breakpointWidth1: ContainerBreakpointWidth,
                                   breakpointWidth2: ContainerBreakpointWidth) => {
   const keys = Object.keys(BREAKPOINTS);
   const keyIndex1 = keys.indexOf(breakpointWidth1);
   const keyIndex2 = keys.indexOf(breakpointWidth2);

   return keyIndex1 - keyIndex2;
};

export const getNextSmallerBreakpoint = (breakpointWidth: ContainerBreakpointWidth): ContainerBreakpointWidth | null => {

   const keys = Object.keys(BREAKPOINTS);
   const keyIndex = keys.indexOf(breakpointWidth);
   if (keyIndex > 0) {
      return keys[keyIndex - 1] as ContainerBreakpointWidth;
   }
   return null;
};

export const getNextLargerBreakpoint = (breakpointWidth: ContainerBreakpointWidth): ContainerBreakpointWidth | null => {

   const keys = Object.keys(BREAKPOINTS);
   const keyIndex = keys.indexOf(breakpointWidth);
   if (keyIndex < (keys.length - 1)) {
      return keys[keyIndex + 1] as ContainerBreakpointWidth;
   }
   return null;
};

export const useContainerBreakpoints = (): [Ref<any>, SizeObservableSize | null, ContainerBreakpointWidth | null] => {

   const [currentBreakpoint, setCurrentBreakpoint] = useState<ContainerBreakpointWidth | null>(null);
   const [dimensions, setDimensions] = useState<SizeObservableSize | null>(null);
   const curNodeRef = useRef<HTMLElement | null>(null);

   const setDimensionsDebounced = useCallback(() => {
      return debounce(setDimensions, 250);
   },[setDimensions]);

   useEffect(()=>{
      if (dimensions && dimensions.width > 0) {
         setCurrentBreakpoint(getCurrentBreakpoint(dimensions.width));
      }
   }, [dimensions]);

   const dimensionsRef = useCallback((domNode: HTMLElement | null) => {
      if (domNode && curNodeRef.current !== domNode) {
         curNodeRef.current = domNode;
         const curDimensions = domNode.getBoundingClientRect();
         setDimensions(curDimensions);
      }
   }, []);

   useLayoutEffect(() => {
      const updateSize = () => {
         if (curNodeRef && curNodeRef.current) {
            const curDimensions = curNodeRef.current.getBoundingClientRect();
            setDimensionsDebounced()(curDimensions);
         }
      };
      window.addEventListener("resize", updateSize);
      return () =>
         window.removeEventListener("resize", updateSize);
   }, [setDimensionsDebounced]);

   return [dimensionsRef, dimensions, currentBreakpoint];
};
